import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import {BLINK_FILTER_HANDOFF,BLINK_HANDOFF_HASH_KEY,BLINK_HANDOFF_READY,BLINK_HANDOFF_SESSION_KEY,BLINK_HANDOFF_SOURCE,BlinkFilterHandoffFilters,BlinkFilterHandoffMessage,BlinkHandoffReadyMessage,BlinkPendingHandoff} from '../constants/blink-handoff.constants';

function originFromEnvUrl(url: string | undefined): string | null {
  if (!url?.trim()) return null;
  try {
    return new URL(url.trim()).origin;
  } catch {
    return null;
  }
}

export type BlinkHandoffHandler = (
  filters: BlinkFilterHandoffFilters,
  autoSearch: boolean
) => void;

@Injectable({ providedIn: 'root' })
export class BlinkHandoffService implements OnDestroy {
  private handler: BlinkHandoffHandler | null = null;
  private listening = false;
  private handoffReceived = false;
  /** Last successfully processed handoff hash payload (allows a later distinct hash in same SPA). */
  private lastCapturedEncoded: string | null = null;
  private readyPulseTimers: ReturnType<typeof setTimeout>[] = [];
  private readonly onMessage = (e: MessageEvent) => this.handleMessage(e);
  constructor() {
    this.captureHashHandoffIntoSession();
  }

  ngOnDestroy(): void {
    this.stopListening();
  }

  expectingHandoff(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      return window.opener != null && !window.opener.closed;
    } catch {
      return false;
    }
  }

  hasReceivedHandoff(): boolean {
    return this.handoffReceived;
  }

  /**
   * Read hash → sessionStorage (once per distinct payload), then return and clear any pending handoff.
   * Call before default BLINK search so Jump/icon filters apply first.
   */
  consumePendingHandoff(): BlinkPendingHandoff | null {
    this.captureHashHandoffIntoSession();
    return this.readAndClearSessionHandoff();
  }

  /** True if hash or session still has an unconsumed handoff (before consume). */
  hasPendingHandoff(): boolean {
    this.captureHashHandoffIntoSession();
    try {
      return !!sessionStorage.getItem(BLINK_HANDOFF_SESSION_KEY);
    } catch {
      return false;
    }
  }

  startListening(onHandoff: BlinkHandoffHandler): void {
    if (typeof window === 'undefined') return;
    this.handler = onHandoff;
    this.captureHashHandoffIntoSession();
    if (!this.listening) {
      window.addEventListener('message', this.onMessage);
      this.listening = true;
    }
    if (this.expectingHandoff()) {
      this.signalReady();
      [400, 1000, 2000].forEach((ms) => {
        this.readyPulseTimers.push(
          setTimeout(() => {
            if (!this.handoffReceived) {
              this.signalReady();
            }
          }, ms)
        );
      });
    }
  }

  stopListening(): void {
    this.readyPulseTimers.forEach((t) => clearTimeout(t));
    this.readyPulseTimers = [];
    if (typeof window !== 'undefined' && this.listening) {
      window.removeEventListener('message', this.onMessage);
      this.listening = false;
    }
    this.handler = null;
  }

  private captureHashHandoffIntoSession(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const rawHash = (window.location.hash || '').replace(/^#/, '');
    if (!rawHash) {
      return;
    }
    const params = new URLSearchParams(rawHash);
    const encoded = params.get(BLINK_HANDOFF_HASH_KEY);
    if (!encoded) {
      return;
    }
    // Same payload already processed (e.g. refresh after clear) — ignore.
    if (encoded === this.lastCapturedEncoded) {
      this.clearHashParam();
      return;
    }
    try {
      const parsed = JSON.parse(decodeURIComponent(encoded));
      const filters =
        parsed?.filters && typeof parsed.filters === 'object' ? parsed.filters : {};
      const autoSearch = parsed?.autoSearch !== false;
      // Require exact source (missing/wrong source → no session write).
      if (parsed?.source !== BLINK_HANDOFF_SOURCE) {
        this.lastCapturedEncoded = encoded;
        this.clearHashParam();
        return;
      }
      sessionStorage.setItem(
        BLINK_HANDOFF_SESSION_KEY,
        JSON.stringify({ filters, autoSearch, ts: Date.now() })
      );
      this.lastCapturedEncoded = encoded;
    } catch (err) {
      console.warn('Failed to parse BLINK handoff hash', err);
      this.lastCapturedEncoded = encoded;
    }
    this.clearHashParam();
  }

  private clearHashParam(): void {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch {
      try {
        window.location.hash = '';
      } catch {
        /* ignore */
      }
    }
  }

  private readAndClearSessionHandoff(): BlinkPendingHandoff | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(BLINK_HANDOFF_SESSION_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(BLINK_HANDOFF_SESSION_KEY);
      const parsed = JSON.parse(raw);
      const filters =
        parsed?.filters && typeof parsed.filters === 'object' ? parsed.filters : {};
      const autoSearch = parsed?.autoSearch !== false;
      this.handoffReceived = true;
      return { filters, autoSearch };
    } catch {
      try {
        sessionStorage.removeItem(BLINK_HANDOFF_SESSION_KEY);
      } catch {
        /* ignore */
      }
      return null;
    }
  }

  private signalReady(): void {
    if (!this.expectingHandoff()) return;
    const vipOrigin = originFromEnvUrl((environment as { vipUrl?: string }).vipUrl);
    if (!vipOrigin || !window.opener) return;
    const payload: BlinkHandoffReadyMessage = {
      type: BLINK_HANDOFF_READY,
      ts: Date.now(),
    };
    try {
      window.opener.postMessage(payload, vipOrigin);
    } catch {
      /* ignore */
    }
  }

  private handleMessage(e: MessageEvent): void {
    const data = e.data as BlinkFilterHandoffMessage | undefined;
    if (!data || data.type !== BLINK_FILTER_HANDOFF) return;
    if (data.source !== BLINK_HANDOFF_SOURCE) return;
    const vipOrigin = originFromEnvUrl((environment as { vipUrl?: string }).vipUrl);
    if (!vipOrigin || e.origin !== vipOrigin) return;
    const filters = data.filters && typeof data.filters === 'object' ? data.filters : {};
    const autoSearch = data.autoSearch !== false;
    if (this.handoffReceived) {
      return;
    }
    this.handoffReceived = true;
    this.readyPulseTimers.forEach((t) => clearTimeout(t));
    this.readyPulseTimers = [];
    try {
      sessionStorage.setItem(
        BLINK_HANDOFF_SESSION_KEY,
        JSON.stringify({ filters, autoSearch, ts: Date.now() })
      );
    } catch {
      /* ignore */
    }
    this.handler?.(filters, autoSearch);
  }
}
